import { Alert, Box, Heading3, Text } from "@nulogy/components";
import FieldRow from "components/common/FieldRow";
import Checkbox from "components/controls/Checkbox";
import FormButtons from "components/controls/FormButtons";
import Input from "components/controls/Input";
import Select from "components/controls/Select";
import SettingsForm from "components/controls/SettingsForm";
import TimePicker from "components/controls/TimePicker";
import PageWithBreadcrumbs from "components/pages/PageWithBreadcrumbs";
import UserIndexView from "components/pages/user/UserIndexView";
import omit from "lodash/omit";
import Codenames from "policies/Codenames";
import React from "react";
import DateTime from "utils/DateTime";
import Time from "utils/Time";
import ResetSiteContainer from "./ResetSiteContainer";

const formatTime = (time) => DateTime.format(new Date(`2000-01-01 ${time}`), "HH:mm:ss");

const ErrorMessage = ({ error }) => {
  if (!error) return "";

  return (
    <Alert marginBottom="16px" type="danger">
      {error}
    </Alert>
  );
};

export default function SiteEditView({
  companies,
  companyId,
  site,
  timeZones,
  onSave,
  onCancel,
  onDelete,
  canCreateUser,
  canUpdateCompany,
  createUserPath,
  nulogyAdmin,
  error,
  setError,
}) {
  const { id, company, name, users } = site;
  const initialValues = {
    ...omit(site, "settings"),
    company: { id: companyId || company?.id },
    externalAccountUuid: site.externalAccountUuid || "",
    externalId: site.externalId || "",
    seedSampleData: false,
    settings: JSON.parse(site.settings),
    startOfDay: Time.to12Hour(site.startOfDay),
  };

  const companyOptions = companies.map((companyOption) => ({ label: companyOption.name, value: companyOption.id }));

  const handleSave = (values) => {
    const siteValues = omit(values, ["settings", "startOfDay"]);
    const settings = JSON.stringify(values.settings);
    const startOfDay = formatTime(values.startOfDay);

    return onSave({ ...siteValues, settings, startOfDay });
  };

  const timeZoneOptions = timeZones.map((timeZone) => {
    const { displayNameWithOffset, displayName } = timeZone;

    return { label: displayNameWithOffset, value: displayName };
  });

  const title = id ? `Update site - ${name}` : "Create site";

  const validate = (data) => {
    if (!data.startOfDay) {
      setError("Must select start of day");
    } else {
      setError(false);
    }
  };

  return (
    <PageWithBreadcrumbs
      className="spec-site-create-edit-page"
      extraBreadcrumbs={[{ label: "Sites", path: "/sites" }]}
      title={title}
    >
      <SettingsForm id="site" initialValues={initialValues} onSave={handleSave} validate={validate}>
        <ErrorMessage error={error} />
        {!id || nulogyAdmin ? (
          <FieldRow>
            <Select
              className="spec-site-company"
              field="company[id]"
              labelText="Company"
              maxHeight="250"
              options={companyOptions}
            />
          </FieldRow>
        ) : (
          <FieldRow>
            <Text fontSize="small" fontWeight="bold">
              Company name
            </Text>
            <Text>{company.name}</Text>
          </FieldRow>
        )}
        <FieldRow>
          <Input data-testid="site-name" field="name" labelText="Name" />
        </FieldRow>
        <FieldRow>
          <Select
            className="spec-time-zone"
            field="timeZoneDisplayName"
            labelText="Time zone"
            maxHeight="250"
            options={timeZoneOptions}
          />
        </FieldRow>
        <FieldRow>
          <TimePicker className="spec-time-picker" field="startOfDay" labelText="Start of day" />
        </FieldRow>
        <FieldRow>
          <Input
            data-testid="site-external-id"
            disabled={!nulogyAdmin}
            field="externalId"
            labelText={`${Codenames.Packmanager} site UUID`}
          />
        </FieldRow>
        <FieldRow>
          <Input
            data-testid="external-account-id"
            disabled={!nulogyAdmin}
            field="externalAccountUuid"
            labelText={`${Codenames.Packmanager} account UUID`}
          />
        </FieldRow>
        <Box marginBottom="x2">
          <Heading3>Site settings</Heading3>
          <Checkbox field="settings[productLedGrowthCustomer]" labelText="Product led growth customer" />
        </Box>
        <Box marginBottom="x2">
          <Heading3>Dev toggles</Heading3>
          {/* To add a dev toggle:
           <Checkbox field="settings[enableLineOrdering]" labelText="Enable line ordering" />
           */}
        </Box>
        {!id && (
          <Box data-testid="sample-data" marginBottom="x2">
            <Heading3>Sample Data</Heading3>
            <Checkbox field="seedSampleData" labelText="Populate site with sample data" />
          </Box>
        )}
        <FormButtons disabled={error} onCancel={onCancel} onDelete={id ? onDelete : null} />
      </SettingsForm>

      {id && (
        <>
          <hr />
          <Box pt="x2">
            <ResetSiteContainer id={id} />
          </Box>
          <hr />
          <Box pt="x2">
            <UserIndexView
              canCreateUser={canCreateUser}
              canUpdateCompany={canUpdateCompany}
              createUserPath={createUserPath}
              simpleView
              users={users}
            />
          </Box>
        </>
      )}
    </PageWithBreadcrumbs>
  );
}
